import React, { Children, cloneElement, PureComponent } from 'react';
import { debounce, isEqual } from 'lodash';
import PropTypes from 'prop-types';

import {
	createGoogleMap,
	getDistance,
	locationsToBounds
} from 'utils/search/autocomplete';

export default class GoogleMap extends PureComponent {
	constructor(props) {
		super(props);

		this.state = {
			map: null,
			mapZoom: null
		};
	}

	componentDidMount() {
		const { onLoad, radius } = this.props;
		const options = {
			radius: radius || 15,
			center: this.props.center,
			onDragEnd: debounce(this.onMapMove, 400)
		};

		createGoogleMap(this.instance, options)
		.then(({ google, map }) => {
			this.setState({ google, map });
			if (this.props.locationBounds) {
				this.setBounds();
			}

			this.setState({ mapZoom: map.getZoom() });

			if (onLoad) {
				onLoad(true);
			}
		});
	}

	componentDidUpdate(prevProps) {
		const { center, locationBounds } = this.props;
		const { google } = this.state;
		if (!isEqual(center, prevProps.center)) {
			if (locationBounds) {
				this.setBounds();
			} else {
				this.state.map.setCenter(new google.maps.LatLng(center.latitude, center.longitude));
			}
		}
	}

	onMapMove = () => {
		const { map, mapZoom } = this.state;
		const center = map.getCenter();
		const zoom = map.getZoom();
		const dX = getDistance(center.lat(), center.lng(), center.lat(), this.props.center.longitude);
		const dY = getDistance(center.lat(), center.lng(), this.props.center.latitude, center.lng());

		if (this.props.onMove && (dX > 2 || dY > 2 || mapZoom !== zoom)) {
			this.props.onMove({ latitude: center.lat(), longitude: center.lng() });
			this.setState({ mapZoom: zoom });
		}
	}

	setBounds = () => {
		const { google } = this.state;
		const { center, locationBounds } = this.props;
		const bounds = locationsToBounds(google, [center, ...locationBounds]);

		// Don't zoom in too far on only one marker
		if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
			const extendPoint1 = new google.maps.LatLng(
				bounds.getNorthEast().lat() + 0.001,
				bounds.getNorthEast().lng() + 0.001
			);
			const extendPoint2 = new google.maps.LatLng(
				bounds.getNorthEast().lat() - 0.001,
				bounds.getNorthEast().lng() - 0.001
			);
			bounds.extend(extendPoint1);
			bounds.extend(extendPoint2);
		}

		this.state.map.fitBounds(bounds);
	}

	bindRef = (instance) => { this.instance = instance; }

	render() {
		const { google, map } = this.state;
		const { children } = this.props;
		return (
			<div className="google-map-container">
				<div className="google-map" ref={this.bindRef}>
					{!!map &&
						Children.toArray(children).map(
							(child) => cloneElement(child, { google, map })
						)
					}
				</div>
			</div>
		);
	}
}

GoogleMap.propTypes = {
	children: PropTypes.node,
	radius: PropTypes.number,
	center: PropTypes.shape({
		latitude: PropTypes.number,
		longitude: PropTypes.number
	}),
	locationBounds: PropTypes.arrayOf(
		PropTypes.shape({
			latitude: PropTypes.number,
			longitude: PropTypes.number
		}),
	),
	onLoad: PropTypes.func,
	onMove: PropTypes.func
};
